import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Eye, X } from "lucide-react";
import { ProceedingOffererDetail } from "@/features/rfq/components/ProceedingOffererDetail";
import type { PublicProceeding } from "@/features/rfq/api/proceedings";

/**
 * Modal „Widok oferenta" na panelu postępowania — wzorowany na widoku podwykonawcy
 * przy zamówieniu.
 *
 * Renderuje TEN SAM komponent, który widzi firma, na danych z publicznego serializera.
 * Gdyby to była osobna makieta, pokazywałaby nasze wyobrażenie o tym widoku zamiast
 * samego widoku — a różnica ujawniłaby się dopiero przy pierwszej reklamacji.
 * Nawigacja wewnętrzna jest zablokowana; pobieranie plików działa normalnie.
 */

interface Props {
  proceeding: PublicProceeding;
  open: boolean;
  onClose: () => void;
}

export function ProceedingOffererViewModal({ proceeding, open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const guardLinks = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") ?? "";
    if (anchor.target === "_blank" || href.startsWith("tel:") || href.startsWith("mailto:")) return;
    e.preventDefault();
    e.stopPropagation();
  };

  return createPortal(
    <div data-modal-layer className="fixed inset-0 z-50 flex bg-black/50 p-4" onClick={onClose}>
      <div
        className="m-auto flex h-full max-h-[92vh] w-full max-w-[1000px] flex-col border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <Eye size={16} className="shrink-0 text-[var(--color-primary)]" />
            <div className="min-w-0">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
                Widok oferenta
              </h2>
              <p className="truncate text-xs text-[var(--color-text-muted)]">
                Dokładnie to widzi podwykonawca dopuszczony do {proceeding.number} — bez ceny
                maksymalnej, jeśli ukryta, bez trybu dostępu i bez przedsięwzięcia.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6" onClickCapture={guardLinks}>
          <ProceedingOffererDetail proceeding={proceeding} preview />
        </div>
      </div>
    </div>,
    document.body,
  );
}
